/**
 * Reusable 1:1 chat pagination helpers.
 *
 * Matrix paginates raw timeline events (membership, receipts, call signalling, …).
 * The UI pages by *displayable messages* and treats the SDK pagination token as
 * the source of truth for “has more”, not raw event counts.
 *
 * The chat hook keeps a lookahead buffer: older messages are fetched into memory
 * before the user reaches the top, then revealing a page is a local window shift.
 */

import { EventTimeline } from 'matrix-js-sdk';
import {
  captureChatException,
  isMatrixExpectedError,
  isMatrixMembershipForbiddenError,
} from 'shared/sentry';
import { MESSAGE_CONSTANTS } from '../../constants/message';
import { isAbortError } from '../isAbortError.js';
import {
  ENCRYPTED_EVENT_TYPE,
  getRoomMessageBody,
  isReplacementEvent,
  isRoomMessageEvent,
} from './matrixRoomUtils';

export const CHAT_MESSAGE_PAGE_SIZE = MESSAGE_CONSTANTS.INITIAL_VISIBLE_MESSAGES;

/**
 * Soft cap for in-memory displayable chat rows per room (visible window +
 * already-fetched older/newer history). Shared by trim + the room messages hook.
 */
export const CHAT_MESSAGE_BUFFER_MAX = 1000;

/**
 * Raw events per /messages request. Sized so one UI page of displayable
 * messages typically arrives in 1–2 round trips despite non-message timeline noise.
 */
export const CHAT_RAW_EVENT_BATCH_SIZE = 100;

/** Keep at least one extra UI page in memory so scroll-up is a local reveal. */
const CHAT_PREFETCH_MIN_OLDER = CHAT_MESSAGE_PAGE_SIZE;

/** More passes so membership/receipt/call noise does not abort a UI page early. */
const MAX_PAGINATION_PASSES = 16;

export const getMessageStableKey = (message) =>
  message?.eventId || message?.txnId || message?.id || '';

const collectStableKeys = (messages, into = new Set()) => {
  if (!Array.isArray(messages)) return into;
  for (let i = 0; i < messages.length; i += 1) {
    const key = getMessageStableKey(messages[i]);
    if (key) into.add(key);
    const txnId = messages[i]?.txnId;
    if (txnId) into.add(txnId);
  }
  return into;
};

export const findMessageIndexByKey = (messages, key) => {
  if (!key || !Array.isArray(messages)) return -1;
  return messages.findIndex((message) => getMessageStableKey(message) === key);
};

export const getLiveTimeline = (room) => room?.getLiveTimeline?.() || null;

export const getLiveTimelineEventCount = (room) =>
  getLiveTimeline(room)?.getEvents?.()?.length ?? 0;

export const getBackwardPaginationToken = (room) => {
  const timeline = getLiveTimeline(room);
  if (!timeline?.getPaginationToken) return null;
  const token = timeline.getPaginationToken(EventTimeline.BACKWARDS);
  if (token == null || token === '') return null;
  return token;
};

export const canPaginateBackwards = (room) => getBackwardPaginationToken(room) != null;

/**
 * Count events that become chat rows (messages + encrypted placeholders).
 * Matches `buildUiMessagesFromRoom` filters so pagination stops at a real page.
 */
export const countDisplayableTimelineMessages = (room) => {
  const rawEvents = getLiveTimeline(room)?.getEvents?.() || room?.timeline || [];
  if (!Array.isArray(rawEvents)) return 0;

  let count = 0;
  for (let i = 0; i < rawEvents.length; i += 1) {
    const event = rawEvents[i];
    if (isReplacementEvent(event)) continue;
    if (event.getType?.() === ENCRYPTED_EVENT_TYPE && !isRoomMessageEvent(event)) {
      count += 1;
      continue;
    }
    if (isRoomMessageEvent(event) && getRoomMessageBody(event)) count += 1;
  }
  return count;
};

export const getVisibleMessageWindow = (messages, oldestVisibleKey, pageSize = CHAT_MESSAGE_PAGE_SIZE) => {
  if (!Array.isArray(messages) || messages.length === 0) return [];
  const size = pageSize > 0 ? pageSize : CHAT_MESSAGE_PAGE_SIZE;

  if (!oldestVisibleKey) {
    return messages.length <= size ? messages : messages.slice(-size);
  }

  const idx = findMessageIndexByKey(messages, oldestVisibleKey);
  if (idx < 0) {
    return messages.length <= size ? messages : messages.slice(-size);
  }
  return messages.slice(idx);
};

export const getOldestKeyForLatestPage = (messages, pageSize = CHAT_MESSAGE_PAGE_SIZE) => {
  if (!Array.isArray(messages) || messages.length === 0) return null;
  const size = pageSize > 0 ? pageSize : CHAT_MESSAGE_PAGE_SIZE;
  const startIdx = Math.max(0, messages.length - size);
  return getMessageStableKey(messages[startIdx]) || null;
};

const countOlderLocalMessages = (
  messages,
  oldestVisibleKey,
  pageSize = CHAT_MESSAGE_PAGE_SIZE
) => {
  if (!Array.isArray(messages) || messages.length === 0) return 0;
  const size = pageSize > 0 ? pageSize : CHAT_MESSAGE_PAGE_SIZE;
  if (!oldestVisibleKey) return Math.max(0, messages.length - size);
  const idx = findMessageIndexByKey(messages, oldestVisibleKey);
  if (idx < 0) return Math.max(0, messages.length - size);
  return idx;
};

export const hasOlderLocalMessages = (messages, oldestVisibleKey, pageSize = CHAT_MESSAGE_PAGE_SIZE) =>
  countOlderLocalMessages(messages, oldestVisibleKey, pageSize) > 0;

export const shouldPrefetchOlderMessages = ({
  messages,
  oldestVisibleKey,
  serverHasMore,
  pageSize = CHAT_MESSAGE_PAGE_SIZE,
  minOlder = CHAT_PREFETCH_MIN_OLDER,
  maxCount,
}) => {
  if (!serverHasMore) return false;
  if (!Array.isArray(messages) || messages.length === 0) return false;
  if (typeof maxCount === 'number' && maxCount > 0 && messages.length >= maxCount) {
    return false;
  }
  return countOlderLocalMessages(messages, oldestVisibleKey, pageSize) < minOlder;
};

/**
 * Keep previous UI message object identity when content is unchanged so
 * memoized bubbles do not re-render during background backfill.
 */
const reuseStableUiMessages = (previous, next) => {
  if (!Array.isArray(next) || next.length === 0) return next;
  if (!Array.isArray(previous) || previous.length === 0) return next;

  const previousByKey = new Map();
  for (let i = 0; i < previous.length; i += 1) {
    const key = getMessageStableKey(previous[i]);
    if (key) previousByKey.set(key, previous[i]);
  }

  return next.map((message) => {
    const key = getMessageStableKey(message);
    const existing = key ? previousByKey.get(key) : null;
    if (
      existing &&
      existing.message === message.message &&
      Boolean(existing.pending) === Boolean(message.pending) &&
      Boolean(existing.failed) === Boolean(message.failed) &&
      existing.eventId === message.eventId
    ) {
      return existing;
    }
    return message;
  });
};

/**
 * Rebuilds from the live timeline can omit rows we already hold (pending sends,
 * or older pages outside the raw-event window). Reattach those rows in order.
 */
export const mergeRetainedUiMessages = (previous, built) => {
  const reused = reuseStableUiMessages(previous, built);
  if (!Array.isArray(previous) || previous.length === 0) return reused;
  if (!Array.isArray(reused) || reused.length === 0) return previous;

  const builtKeys = collectStableKeys(reused);

  const older = [];
  const newer = [];
  const firstTs = Number(reused[0]?.ts) || 0;
  const lastTs = Number(reused[reused.length - 1]?.ts) || 0;

  for (let i = 0; i < previous.length; i += 1) {
    const message = previous[i];
    const key = getMessageStableKey(message);
    if (key && builtKeys.has(key)) continue;
    if (message.txnId && builtKeys.has(message.txnId)) continue;
    const ts = Number(message.ts) || 0;
    if (message.pending || ts >= lastTs) newer.push(message);
    else if (ts <= firstTs) older.push(message);
    else newer.push(message);
  }

  if (!older.length && !newer.length) return reused;
  return [...older, ...reused, ...newer];
};

/**
 * Prepend incoming rows that are not already in `current` (by eventId/txnId/id).
 */
export const prependUniqueUiMessages = (current, incoming) => {
  const base = Array.isArray(current) ? current : [];
  if (!Array.isArray(incoming) || incoming.length === 0) {
    return { list: base, added: [] };
  }

  const existing = collectStableKeys(base);
  const added = [];
  for (let i = 0; i < incoming.length; i += 1) {
    const message = incoming[i];
    const key = getMessageStableKey(message);
    if (!key || existing.has(key)) continue;
    if (message.txnId && existing.has(message.txnId)) continue;
    existing.add(key);
    if (message.txnId) existing.add(message.txnId);
    added.push(message);
  }

  if (!added.length) return { list: base, added };
  return { list: [...added, ...base], added };
};

/**
 * Bound the in-memory list without throwing away already-fetched older history.
 *
 * Keeps the visible window and as much older/newer history as `maxCount` allows.
 * `olderBuffer` is accepted for call-site compatibility but no longer limits
 * retention to a single page (that caused false "Beginning of conversation").
 *
 * @param {Array} list
 * @param {string|null} oldestVisibleKey
 * @param {number} [_olderBuffer]
 * @param {number} [maxCount]
 */
export const trimMessagesAroundVisible = (
  list,
  oldestVisibleKey,
  _olderBuffer = CHAT_MESSAGE_PAGE_SIZE,
  maxCount = CHAT_MESSAGE_BUFFER_MAX
) => {
  if (!Array.isArray(list) || list.length === 0) return list;
  const max = typeof maxCount === 'number' && maxCount > 0 ? Math.floor(maxCount) : CHAT_MESSAGE_BUFFER_MAX;
  if (list.length <= max) return list;

  const visibleIdx = findMessageIndexByKey(list, oldestVisibleKey);
  if (visibleIdx < 0) return list.slice(-max);

  const newerCount = list.length - visibleIdx;
  const olderKeep = Math.max(0, max - newerCount);
  const start = Math.max(0, visibleIdx - olderKeep);
  return list.slice(start);
};

export const shiftVisibleWindowOlder = (messages, oldestVisibleKey, pageSize = CHAT_MESSAGE_PAGE_SIZE) => {
  if (!Array.isArray(messages) || messages.length === 0) {
    return { nextKey: null, loaded: 0 };
  }

  const size = pageSize > 0 ? pageSize : CHAT_MESSAGE_PAGE_SIZE;
  const currentIdx = findMessageIndexByKey(messages, oldestVisibleKey);
  const implicitStart = Math.max(0, messages.length - size);
  const fromIdx = currentIdx >= 0 ? currentIdx : implicitStart;
  const nextIdx = Math.max(0, fromIdx - size);

  return {
    nextKey: getMessageStableKey(messages[nextIdx]) || null,
    loaded: fromIdx - nextIdx,
  };
};

/**
 * One backwards page of the live timeline via GET /rooms/{roomId}/messages.
 *
 * Spec (CS API): an empty `chunk` is not exhaustion — keep paging until `end`
 * is omitted. /sync may omit `prev_batch` when nothing older exists. Spec v1.3:
 * omitting `from` with dir=b starts at the last visible event. matrix-js-sdk
 * v39 `paginateEventTimeline` still calls /messages when the token is null
 * (omits `from`), which restarts from the latest events and loops — so we do
 * not call it without a token. Prefer it over `scrollback()`, which no-ops on
 * null `room.oldState.paginationToken` and treats an empty chunk as the end.
 *
 * `exhausted` is true only after a completed /messages page with no `end`
 * (SDK also treats `end === start`). A missing token without that request
 * is “cannot paginate now”, not confirmed start-of-room.
 *
 * @returns {Promise<{ progressed: boolean, hasMore: boolean, exhausted: boolean, error?: boolean }>}
 */
export async function paginateLiveTimelineBackwards(
  client,
  room,
  rawLimit = CHAT_RAW_EVENT_BATCH_SIZE
) {
  const timeline = getLiveTimeline(room);
  if (!client || !room || !timeline) {
    return { progressed: false, hasMore: false, exhausted: false };
  }

  const roomId = room?.roomId;
  // Room temporarily missing from the store is not end-of-history.
  if (roomId && typeof client.getRoom === 'function' && !client.getRoom(roomId)) {
    return { progressed: false, hasMore: true, exhausted: false };
  }

  const tokenBefore = getBackwardPaginationToken(room);
  if (tokenBefore == null) {
    // Missing token ≠ confirmed start. Caller must not show "Beginning of conversation".
    return { progressed: false, hasMore: true, exhausted: false };
  }

  const beforeCount = getLiveTimelineEventCount(room);
  const limit =
    typeof rawLimit === 'number' && rawLimit > 0
      ? Math.floor(rawLimit)
      : CHAT_RAW_EVENT_BATCH_SIZE;

  let sdkHasMore = false;

  try {
    if (typeof client.paginateEventTimeline === 'function') {
      sdkHasMore = Boolean(
        await client.paginateEventTimeline(timeline, { backwards: true, limit })
      );
    } else if (typeof client.scrollback === 'function') {
      await client.scrollback(room, limit);
      sdkHasMore = getBackwardPaginationToken(room) != null;
    } else {
      return { progressed: false, hasMore: false, exhausted: false };
    }
  } catch (error) {
    if (!isAbortError(error) && !isMatrixExpectedError(error)) {
      captureChatException(error, {
        tags: { feature: 'matrix-chat', action: 'paginate-backwards' },
      });
    }
    // CS API: non-member + previews disabled → M_FORBIDDEN. Stop paging; do not retry.
    if (isMatrixMembershipForbiddenError(error)) {
      return { progressed: false, hasMore: false, exhausted: true, error: true };
    }
    // Transient failures must not be treated as end-of-history.
    return { progressed: false, hasMore: true, exhausted: false, error: true };
  }

  const afterCount = getLiveTimelineEventCount(room);
  const tokenAfter = getBackwardPaginationToken(room);
  const progressed = afterCount > beforeCount;

  // Live prev_batch still present ⇒ keep paging (noise-only pages are normal).
  if (tokenAfter != null) {
    return { progressed, hasMore: true, exhausted: false };
  }

  // Token cleared by the SDK after this /messages call ⇒ end of timeline.
  return {
    progressed,
    hasMore: Boolean(sdkHasMore),
    exhausted: !sdkHasMore,
  };
}

/**
 * Fetch older events until the live timeline has `targetCount` displayable messages
 * (or history is exhausted).
 */
export async function paginateUntilUiMessageCount({
  client,
  room,
  getUiMessageCount,
  targetCount,
  rawBatchSize = CHAT_RAW_EVENT_BATCH_SIZE,
  maxPasses = MAX_PAGINATION_PASSES,
}) {
  const readCount = () => {
    if (typeof getUiMessageCount === 'function') return getUiMessageCount(room);
    return countDisplayableTimelineMessages(room);
  };

  if (!client || !room) {
    return { count: 0, hasMore: false, exhausted: false };
  }

  let hasMore = canPaginateBackwards(room);

  for (let pass = 0; pass < maxPasses; pass += 1) {
    const count = readCount();
    if (count >= targetCount) {
      return { count, hasMore: canPaginateBackwards(room), exhausted: false };
    }

    // Missing pagination token is ambiguous (sync may not have applied yet) — not
    // confirmed start-of-room. Keep hasMore true so the UI does not show
    // "Beginning of conversation" until a /messages page omits `end`.
    if (!canPaginateBackwards(room)) {
      return { count, hasMore: true, exhausted: false };
    }

    const result = await paginateLiveTimelineBackwards(client, room, rawBatchSize);
    hasMore = Boolean(result.hasMore);
    if (result.error) {
      return { count: readCount(), hasMore: true, exhausted: false, error: true };
    }
    if (result.exhausted || !hasMore) {
      return { count: readCount(), hasMore: false, exhausted: Boolean(result.exhausted) };
    }
    // Empty `chunk` is not exhaustion while `end` is still present. Keep
    // paging (bounded by maxPasses) so membership/receipt noise does not
    // abort a UI page.
  }

  return {
    count: readCount(),
    hasMore: hasMore || canPaginateBackwards(room),
    exhausted: false,
  };
}

/**
 * Fetch older events until `targetNewCount` additional displayable messages arrive.
 */
export async function paginateUntilNewUiMessages({
  client,
  room,
  getUiMessageCount,
  targetNewCount,
  rawBatchSize = CHAT_RAW_EVENT_BATCH_SIZE,
  maxPasses = MAX_PAGINATION_PASSES,
}) {
  const readCount = () => {
    if (typeof getUiMessageCount === 'function') return getUiMessageCount(room);
    return countDisplayableTimelineMessages(room);
  };

  const startCount = readCount();
  const result = await paginateUntilUiMessageCount({
    client,
    room,
    getUiMessageCount,
    targetCount: startCount + Math.max(1, targetNewCount),
    rawBatchSize,
    maxPasses,
  });

  return {
    loaded: Math.max(0, result.count - startCount),
    hasMore: result.hasMore,
    exhausted: Boolean(result.exhausted),
    count: result.count,
    error: Boolean(result.error),
  };
}
